import { useTranslation } from "react-i18next"
import { AlertCircle, CheckCircle, DownloadIcon } from "lucide-react"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"

import { useDownloadCsvErrors } from "../download-errors"
import { PreviewTable } from "../preview-table"
import type { MergedRow } from "../schemas"

type Props = {
  onReset: () => void
  onStartImport: () => Promise<void>
  rows: MergedRow[]
}

export function StepPreview({ onReset, onStartImport, rows }: Props) {
  const { t } = useTranslation()
  const { downloadErrorCsv } = useDownloadCsvErrors(rows)

  const readyCount = rows.filter(
    (r) => r.parseStatus === "valid" && r.status === "ready",
  ).length
  const errorCount = rows.filter(
    (r) =>
      r.parseStatus === "invalid" ||
      (r.parseStatus === "valid" && r.status === "not_found"),
  ).length

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-3">
        {readyCount > 0 && (
          <Alert>
            <CheckCircle className="size-4" />
            <AlertDescription>
              {t("noCallList.importCSV.preview.readyCount", {
                count: readyCount,
              })}
            </AlertDescription>
          </Alert>
        )}
        {errorCount > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertDescription>
              {t("noCallList.importCSV.preview.errorCount", {
                count: errorCount,
              })}
            </AlertDescription>
          </Alert>
        )}
      </div>

      <PreviewTable rows={rows} />

      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button variant="outline" onClick={onReset}>
            {t("global.button.goBack")}
          </Button>
          {errorCount > 0 && (
            <Button variant="outline" onClick={downloadErrorCsv}>
              <DownloadIcon className="mr-1 size-4" />
              {t("noCallList.importCSV.preview.downloadErrors")}
            </Button>
          )}
        </div>
        <Button onClick={onStartImport} isDisabled={readyCount === 0}>
          {t("noCallList.importCSV.preview.processButton")}
        </Button>
      </div>
    </div>
  )
}
